import { DateTime } from 'luxon';

import { useState } from 'react';

import { createFileRoute } from '@tanstack/react-router';

import {
  type ActiveFilter,
  ActiveFilters,
  FilterBar,
  Group,
  PlanningPageLayoutView,
  SelectInputView,
  Stack,
  StatusFilterChipsView,
} from '@optiro/ui';

import { statusColors } from '@/components/ProjectStatusBadge';
import { useAccounts } from '@/hooks/useAccounts';
import { useCategories } from '@/hooks/useCategories';
import GanttBetaOrchestrator from '@/modules/Planning/Projects/Projects.orchestrator';
import { GanttSearchInput } from '@/modules/Planning/Projects/components/GanttSearchInput/GanttSearchInput';
import { PlanningConnectionStatus } from '@/modules/Planning/components/PlanningConnectionStatus/PlanningConnectionStatus';
import { PlanningPresenceAvatars } from '@/modules/Planning/components/PlanningPresenceAvatars/PlanningPresenceAvatars';
import { ProjectStatus } from '@/types/types';

export const Route = createFileRoute('/app/planning/')({
  component: RouteComponent,
});

const STATUS_OPTIONS: { label: string; value: ProjectStatus; color: string }[] = [
  { label: 'Planlægning', value: 'planning', color: statusColors.planning },
  { label: 'Aktiv', value: 'active', color: statusColors.active },
];

const DEFAULT_STATUSES: ProjectStatus[] = ['planning', 'active'];

function RouteComponent() {
  const [selectedStatuses, setSelectedStatuses] = useState<ProjectStatus[]>(DEFAULT_STATUSES);
  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [focusedDate, setFocusedDate] = useState<DateTime | null>(null);

  const { data: accounts } = useAccounts();
  const { data: categories } = useCategories();

  const managerOptions =
    accounts?.map((a) => ({ value: String(a.id), label: a.name ?? a.email })) ?? [];
  const categoryOptions = categories?.map((c) => ({ value: String(c.id), label: c.name })) ?? [];

  const activeCount = (selectedManagerId ? 1 : 0) + (selectedCategoryId ? 1 : 0);
  const isFiltered = activeCount > 0 || selectedStatuses.length !== DEFAULT_STATUSES.length;

  const clearAll = () => {
    setSelectedStatuses(DEFAULT_STATUSES);
    setSelectedManagerId(null);
    setSelectedCategoryId(null);
  };

  const pills: ActiveFilter[] = [
    ...(selectedManagerId
      ? [
          {
            key: 'manager',
            label: `Leder: ${managerOptions.find((m) => m.value === selectedManagerId)?.label ?? selectedManagerId}`,
            onRemove: () => setSelectedManagerId(null),
          },
        ]
      : []),
    ...(selectedCategoryId
      ? [
          {
            key: 'category',
            label: `Kategori: ${categoryOptions.find((c) => c.value === selectedCategoryId)?.label ?? selectedCategoryId}`,
            onRemove: () => setSelectedCategoryId(null),
          },
        ]
      : []),
  ];

  const handleSearchSelect = (date: DateTime) => {
    setFocusedDate(date);
  };

  const actions = (
    <Group gap="sm">
      <PlanningConnectionStatus />
      <PlanningPresenceAvatars />
    </Group>
  );

  // ── Slim page toolbar — sits under the header, in the maw-constrained section ──
  const toolbar = (
    <Stack gap="xs">
      <Group gap="md" wrap="wrap" align="center">
        <FilterBar
          gap="xs"
          onClear={clearAll}
          hasActiveFilters={isFiltered}
          activeCount={activeCount}
        >
          <GanttSearchInput onSelect={handleSearchSelect} />
          <SelectInputView
            size="xs"
            placeholder="Leder"
            data={managerOptions}
            value={selectedManagerId}
            onChange={setSelectedManagerId}
            w={160}
            nothingFoundMessage="Ingen ledere"
          />
          <SelectInputView
            size="xs"
            placeholder="Kategori"
            data={categoryOptions}
            value={selectedCategoryId}
            onChange={setSelectedCategoryId}
            w={160}
            nothingFoundMessage="Ingen kategorier"
          />
        </FilterBar>
      </Group>

      <StatusFilterChipsView
        options={STATUS_OPTIONS}
        value={selectedStatuses}
        onChange={(v) => setSelectedStatuses(v as ProjectStatus[])}
      />

      <ActiveFilters
        filters={pills}
        onClearAll={() => {
          setSelectedManagerId(null);
          setSelectedCategoryId(null);
        }}
      />
    </Stack>
  );

  return (
    <PlanningPageLayoutView title="Planlægning" actions={actions} toolbar={toolbar}>
      <GanttBetaOrchestrator
        filters={{
          status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
          manager_id: selectedManagerId ?? undefined,
          category_id: selectedCategoryId ?? undefined,
        }}
        focusDate={focusedDate}
        onFocusDateChange={setFocusedDate}
      />
    </PlanningPageLayoutView>
  );
}
